import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { P, roleLabels } from "@/lib/permissions";
import type { User } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Skeleton, Table } from "@/components/ui";
import { useAuth } from "@/state/auth";

// Only roles an account can grant itself. Staff roles are absent because the
// API refuses them, and offering a choice that will be rejected is worse than
// not offering it.
const ASSIGNABLE = [
  { value: "admin", label: "Account admin, manages users and sees billing" },
  { value: "operator", label: "Account user, books and tracks" },
  { value: "shipment_editor", label: "Shipment editor, books only" },
  { value: "viewer", label: "View only, including billing" },
  { value: "viewer_no_billing", label: "View only, no billing" },
  { value: "guest", label: "Guest, tracking only" },
];

export function Team() {
  const { can, user: me } = useAuth();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "operator",
    password: "",
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["team"],
    queryFn: () => api.get<{ data: User[] }>("/v1/users"),
  });

  const invite = useMutation({
    mutationFn: () => api.post<User>("/v1/users", form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team"] });
      setForm({ email: "", first_name: "", last_name: "", role: "operator", password: "" });
      setInviting(false);
      setFields({});
      setError("");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (Object.keys(err.fields).length === 0) setError(err.message);
      } else {
        setError("Could not add that user. Try again.");
      }
    },
  });

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Who can sign in to this account, and what they can do."
        action={
          can(P.manageUsers) && !inviting && <Button onClick={() => setInviting(true)}>Add someone</Button>
        }
      />

      {inviting && (
        <Card className="mb-5">
          <h2 className="mb-4 text-lg font-bold tracking-tight">Add a team member</h2>
          {error && (
            <div className="mb-4">
              <Alert>{error}</Alert>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              error={fields.first_name}
            />
            <Field
              label="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
            <Field
              label="Email address"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={fields.email}
            />
            <Field
              label="Temporary password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              error={fields.password}
              hint="At least 10 characters. They should change it."
            />
            <Select
              label="Role"
              className="sm:col-span-2"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              error={fields.role}
              options={ASSIGNABLE.map((r) => ({ value: r.value, label: r.label }))}
            />
          </div>
          <div className="mt-5 flex gap-2">
            <Button loading={invite.isPending} onClick={() => invite.mutate()}>
              Add user
            </Button>
            <Button variant="secondary" onClick={() => setInviting(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : (
        <Table head={["Name", "Email", "Role", "Last seen"]}>
          {(data?.data ?? []).map((member) => (
            <tr key={member.id}>
              <td className="ws-td font-semibold">
                {member.first_name} {member.last_name}
                {member.id === me?.id && <span className="ml-2 text-xs text-ink-400">you</span>}
              </td>
              <td className="ws-td text-ink-600">{member.email}</td>
              <td className="ws-td">
                <div className="flex items-center gap-1.5">
                  <Badge tone="neutral">{roleLabels[member.role] ?? member.role}</Badge>
                  {!member.is_active && <Badge tone="bad">disabled</Badge>}
                </div>
              </td>
              <td className="ws-td text-ink-500">
                {member.last_login_at ? dateTime(member.last_login_at) : "Never"}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
